/**LAGOSEntityProfile — /state/lagos/entity/:entityId */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLagos } from "@/context/LAGOSContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { toast } from "sonner";
import client from "@/api/client";
import { ArrowLeft, Save, RefreshCw } from "lucide-react";

const fmtN = (v, u="") => { if (!v && v!==0) return "—"; const n=Number(v); return `${n.toLocaleString()} ${u}`.trim(); };
const LABEL_C = { Platinum:"#7c3aed", Gold:"#d97706", Silver:"#64748b", Bronze:"#b45309", Emerging:"#94a3b8" };

export default function LAGOSEntityProfile() {
  const { entityId } = useParams();
  const { selectedPeriod } = useLagos();
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!entityId || !selectedPeriod) return;
    setLoading(true);
    client.get(`/lagos/entity/${entityId}/${selectedPeriod}`)
      .then(r => { setEntity(r.data); setForm(r.data); })
      .catch(() => toast.error("Entity not found"))
      .finally(() => setLoading(false));
  }, [entityId, selectedPeriod]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await client.post(`/lagos/entity/${entityId}/${selectedPeriod}`, form);
      setEntity(r.data.entity || entity);
      toast.success("Entity updated and recalculated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading entity profile…</div>;
  if (!entity) return <div className="p-8 text-red-400 text-sm">Entity not found</div>;

  const e = entity;
  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={()=>navigate("/state/lagos/reporting")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
          <ArrowLeft size={12} /> Registry
        </button>
        <span className="text-slate-300">/</span>
        <h1 className="text-sm font-black text-slate-800">{e.name}</h1>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{background: LABEL_C[e.eco_label]+"18", color: LABEL_C[e.eco_label]}}>{e.eco_label}</span>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {[["tCO₂e Baseline", fmtN(e.total_tco2e_yr,"t"), "#dc2626"],["Reduction Potential",fmtN(e.reduction_potential_tco2e,"t"),"#059669"],["Climate Score",(e.climate_score||0).toFixed(1)+"/100","#1d4ed8"],["Circularity Score",(e.circularity_score||0).toFixed(1)+"/100","#d97706"]].map(([l,v,c])=>(
          <div key={l} className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className="text-[10px] text-slate-400">{l}</p>
            <p className="text-xl font-black mt-1" style={{color:c}}>{v}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SectionCard title="Activity Inputs (Edit)">
          <div className="p-4 space-y-2">
            {[["electricity_kwh","Electricity (kWh/yr)","number"],["diesel_litres","Diesel (L/yr)","number"],["production_output","Production Output","number"],["waste_recycled_t","Waste Recycled (t/yr)","number"],["employees","Employees","number"],["recycling_rate_pct","Recycling Rate (%)","number"],["evidence_level","Evidence Level","select"]].map(([k,l,t])=>(
              <div key={k} className="flex items-center gap-2">
                <label className="text-[10px] text-slate-500 w-44 flex-shrink-0">{l}</label>
                {t==="select"
                  ? <select value={form[k]||""} onChange={ev=>setForm(f=>({...f,[k]:ev.target.value}))} className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 bg-white">
                      {["low","medium","high","measured","estimated","declared"].map(o=><option key={o}>{o}</option>)}</select>
                  : <input type={t} value={form[k]||""} onChange={ev=>setForm(f=>({...f,[k]:ev.target.value}))} className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5" />}
              </div>
            ))}
            <button onClick={handleSave} disabled={saving} className="w-full flex items-center justify-center gap-2 mt-3 bg-[#1d4ed8] text-white text-xs py-2 rounded-lg disabled:opacity-60" data-testid="entity-save">
              {saving ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
              {saving ? "Recalculating…" : "Save & Recalculate"}
            </button>
          </div>
        </SectionCard>
        <SectionCard title="SSOT Calculated Outputs">
          <div className="p-4">
            {[["Sector",(e.sector||"").replace(/_/g," ")],["LGA",e.lga],["Scope 1",fmtN(e.scope1_tco2e,"tCO₂e")],["Scope 2",fmtN(e.scope2_tco2e,"tCO₂e")],["Recycle Credit",fmtN(e.recycle_credit_tco2e,"tCO₂e")],["Carbon Pathway",e.carbon_pathway],["CPA Group",e.cpa_group||"—"],["VCM Eligible",e.vcm_eligible?"Yes":"No"],["Art.6.4 Eligible",e.art6_eligible?"Yes":"No"],["Composite Score",(e.composite_score||0).toFixed(1)],["Data Confidence",(e.data_confidence_score||0)+"/100"]].map(([k,v])=>(
              <div key={k} className="flex justify-between text-xs py-1.5 border-b border-slate-50">
                <span className="text-slate-400">{k}</span><span className="font-medium text-slate-700">{v}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
